import React, { useState, useEffect, useRef } from 'react';
import {
  Autocomplete,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Upgrade as UpgradeIcon,
  ViewModule as CardViewIcon,
  ViewList as TableViewIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ApiService from '../services/api.service';
import useOrganizationSearch from '../hooks/useOrganizationSearch';
import { isPlatformLevel, isPlatformAdmin } from '../utils/roleUtils';
import { getBrandedTableHeadSx } from '../utils/colorUtils';

// TYPING_CENTER and B2B both map exactly one Thaklis org (the create form resolves it from the code).
const mapsOneOrg = (t) => t === 'TYPING_CENTER' || t === 'B2B';

// The API sends an `active` boolean; older payloads carried a `status` string.
const statusOf = (center) => {
  if (typeof center?.active === 'boolean') return center.active ? 'ACTIVE' : 'INACTIVE';
  return center?.status ? String(center.status).toUpperCase() : 'INACTIVE';
};

const AUTO_FILL_FIELDS = ['name', 'email', 'contactPerson', 'phone', 'address'];

const getOrganizationAutoFillValues = (organization) => ({
  name: organization?.name || '',
  email: organization?.email || '',
  contactPerson: organization?.contactPerson || organization?.contact_person || '',
  phone: organization?.contactNumber || organization?.contact_number || '',
  address: organization?.address || ''
});

const clearAutoFilledValuesIfUnchanged = (setFormData, autoFillSnapshotRef) => {
  const snapshot = autoFillSnapshotRef.current;
  if (!snapshot) return;

  autoFillSnapshotRef.current = null;
  setFormData(prev => {
    const stillHasAutoFillValues = AUTO_FILL_FIELDS.every(field => prev[field] === snapshot.values[field]);
    if (!stillHasAutoFillValues) {
      return prev;
    }

    return {
      ...prev,
      name: '',
      email: '',
      contactPerson: '',
      phone: '',
      address: ''
    };
  });
};

// BE-31: VAT details asked at creation; the server starts the ledger at the current filing period
const VAT_DEFAULTS = { vatRegistered: true, trn: '', vatPeriodMonths: 3, vatFirstPeriodMonth: 1 };
const vatPayload = (f) => (f.vatRegistered
  ? {
    vat_registered: true,
    ...(f.trn ? { trn: f.trn.replace(/\s/g, '') } : {}),
    vat_period_months: f.vatPeriodMonths,
    vat_first_period_month: f.vatPeriodMonths === 3 ? f.vatFirstPeriodMonth : 1,
  }
  : { vat_registered: false });

const TypingCentersList = ({ currentUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [searchParams] = useSearchParams();
  const typeFilter = searchParams.get('type'); // 'TYPING_CENTER' or 'PRO'
  const [typingCenters, setTypingCenters] = useState([]);
  const [allCenters, setAllCenters] = useState([]); // unfiltered, to know which internal orgs are promoted
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'promote'
  const [promotionFilter, setPromotionFilter] = useState('ALL'); // ALL | INDEPENDENT | PROMOTED
  const [tenantView, setTenantView] = useState(() => {
    try { return localStorage.getItem('typingCentersView') === 'cards' ? 'cards' : 'table'; }
    catch (e) { return 'table'; }
  });
  // Promote-flow state: pick a B2B, then one of its customer TCs (internal orgs).
  const [b2bList, setB2bList] = useState([]);
  const [selectedB2bId, setSelectedB2bId] = useState('');
  // Only typing-center customers can be promoted; the server resolves null-type legacy rows.
  const {
    options: customerTcs,
    loading: customerTcsLoading,
    onInputChange: onCustomerTcInputChange,
    filterOptions: customerTcFilterOptions
  } = useOrganizationSearch({
    typingCenterId: selectedB2bId,
    type: 'TYPING_CENTER',
    enabled: openDialog && dialogMode === 'promote' && !!selectedB2bId
  });
  const [selectedCustomerTc, setSelectedCustomerTc] = useState(null);
  const [selectedCustomerTcId, setSelectedCustomerTcId] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [matchedOrganization, setMatchedOrganization] = useState(null);
  const [codeMappingError, setCodeMappingError] = useState('');
  const autoFillSnapshotRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    email: '',
    contactPerson: '',
    phone: '',
    address: '',
    organizationType: typeFilter || 'TYPING_CENTER',
    ...VAT_DEFAULTS
  });

  // Update default organizationType when typeFilter changes
  useEffect(() => {
    if (typeFilter) {
      setFormData(prev => ({ ...prev, organizationType: typeFilter }));
    }
  }, [typeFilter]);

  // Determine if user can manage typing centers (platform admin only)
  const isAdmin = isPlatformAdmin(currentUser?.role);

  useEffect(() => {
    fetchTypingCenters();
  }, [typeFilter]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!openDialog) return;
      if (!mapsOneOrg(formData.organizationType)) return;

      try {
        setOrganizationsLoading(true);
        const res = await ApiService.getOrganizations();
        if (res.data && res.data.success) {
          setOrganizations(Array.isArray(res.data.data) ? res.data.data : []);
        } else {
          setOrganizations([]);
        }
      } catch (err) {
        console.error('Error fetching organizations:', err);
        setOrganizations([]);
      } finally {
        setOrganizationsLoading(false);
      }
    };

    fetchOrganizations();
  }, [openDialog, formData.organizationType]);

  useEffect(() => {
    if (!openDialog) {
      setMatchedOrganization(null);
      setCodeMappingError('');
      autoFillSnapshotRef.current = null;
      return;
    }

    if (!mapsOneOrg(formData.organizationType)) {
      setMatchedOrganization(null);
      setCodeMappingError('');
      clearAutoFilledValuesIfUnchanged(setFormData, autoFillSnapshotRef);
      return;
    }

    const code = (formData.code || '').trim().toUpperCase();
    if (!code) {
      setMatchedOrganization(null);
      setCodeMappingError('');
      clearAutoFilledValuesIfUnchanged(setFormData, autoFillSnapshotRef);
      return;
    }

    const match = organizations.find((o) => {
      const organizationCode = o?.code || o?.organization_code || '';
      return organizationCode.toString().trim().toUpperCase() === code;
    });
    if (match) {
      setMatchedOrganization(match);
      setCodeMappingError('');
      const autoFillValues = getOrganizationAutoFillValues(match);
      const previousAutoFillSnapshot = autoFillSnapshotRef.current;
      setFormData(prev => {
        const hasManualEdits =
          previousAutoFillSnapshot?.code === code &&
          AUTO_FILL_FIELDS.some(field => prev[field] !== previousAutoFillSnapshot.values[field]);

        if (hasManualEdits) {
          return prev;
        }

        return {
          ...prev,
          ...autoFillValues
        };
      });
      autoFillSnapshotRef.current = { code, values: autoFillValues };
    } else {
      setMatchedOrganization(null);
      if (!organizationsLoading) {
        setCodeMappingError('Please enter correct code');
        clearAutoFilledValuesIfUnchanged(setFormData, autoFillSnapshotRef);
      }
    }
  }, [formData.code, formData.organizationType, openDialog, organizations, organizationsLoading]);

  const fetchTypingCenters = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.get('/api/typing-centers');
      if (response.data && response.data.success) {
        const allCentersData = response.data.data;
        setAllCenters(Array.isArray(allCentersData) ? allCentersData : []);

        // Filter based on user role — platform users see all, TC users see only their own
        const isPlatformLevel = currentUser?.role === 'PLATFORM_ADMIN' || currentUser?.role === 'PLATFORM_SUPPORT';
        let filteredCenters = allCentersData;
        if (!isPlatformLevel) {
          filteredCenters = allCenters.filter(center => center.id === currentUser?.typingCenterId);
          console.log('🔒 [TypingCentersList] Filtering for TC user:', {
            userRole: currentUser?.role,
            userTypingCenterId: currentUser?.typingCenterId,
            filteredCount: filteredCenters.length
          });
        } else {
          console.log('👑 [TypingCentersList] Platform user - showing all centers:', allCenters.length);
        }

        // Filter by organization type if specified
        if (typeFilter) {
          filteredCenters = filteredCenters.filter(center => {
            const orgType = center.organization_type || center.organizationType;
            return orgType === typeFilter;
          });
          console.log('🏷️ [TypingCentersList] Filtering by type:', typeFilter, 'Count:', filteredCenters.length);
        }

        setTypingCenters(filteredCenters);
      }
    } catch (err) {
      console.error('Error fetching typing centers:', err);
      setError('Failed to load typing centers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCenter = async () => {
    try {
      setLoading(true);
      setError(null);

      if (formData.vatRegistered && formData.trn && formData.trn.replace(/\s/g, '').length !== 15) {
        setError('TRN must be exactly 15 digits');
        return;
      }

      if (mapsOneOrg(formData.organizationType) && !matchedOrganization?.id) {
        setCodeMappingError('Please enter correct code');
        return;
      }

      const payload = {
        name: formData.name,
        code: formData.code,
        email: formData.email,
        contact_person: formData.contactPerson,
        contact_number: formData.phone,
        address: formData.address,
        organization_type: formData.organizationType,
        ...(mapsOneOrg(formData.organizationType) && matchedOrganization?.id
          ? { organization_id: matchedOrganization.id }
          : {}),
        ...vatPayload(formData),
        active: true
      };

      const response = await ApiService.post('/api/typing-centers', payload);

      if (response.data && response.data.success) {
        await fetchTypingCenters();
        setOpenDialog(false);
        setFormData({
          name: '',
          code: '',
          email: '',
          contactPerson: '',
          phone: '',
          address: '',
          organizationType: 'TYPING_CENTER',
          ...VAT_DEFAULTS
        });
        setMatchedOrganization(null);
        autoFillSnapshotRef.current = null;
        setCodeMappingError('');
        // Navigate to the new typing center detail page
        navigate(`/typing-centers/${response.data.data.id}`);
      }
    } catch (err) {
      console.error('Error creating typing center:', err);
      setError(err.response?.data?.message || 'Failed to create typing center');
    } finally {
      setLoading(false);
    }
  };

  // Internal-org ids that already became a real TC (so we disable them in the promote picker).
  const promotedOrgIds = new Set(
    allCenters.map((c) => c.promoted_from_internal_org_id || c.promotedFromInternalOrgId).filter(Boolean)
  );

  const resetForm = () => {
    setFormData({
      name: '', code: '', email: '', contactPerson: '', phone: '', address: '',
      organizationType: typeFilter || 'TYPING_CENTER', ...VAT_DEFAULTS
    });
    setMatchedOrganization(null);
    autoFillSnapshotRef.current = null;
    setCodeMappingError('');
  };

  const openCreateDialog = () => {
    setDialogMode('create');
    resetForm();
    setOpenDialog(true);
  };

  const openPromoteDialog = () => {
    setDialogMode('promote');
    resetForm();
    setFormData((prev) => ({ ...prev, organizationType: 'TYPING_CENTER' }));
    setB2bList(allCenters.filter((c) => (c.organization_type || c.organizationType) === 'B2B'));
    setSelectedB2bId('');
    setSelectedCustomerTc(null);
    setSelectedCustomerTcId('');
    setOpenDialog(true);
  };


  const handleSelectCustomerTc = (org) => {
    setSelectedCustomerTcId(org?.id || '');
    if (org) {
      // Prefill details from the customer TC (its own code still maps a Thaklis org below).
      setFormData((prev) => ({
        ...prev,
        name: org.name || '',
        email: org.email || org.primary_email || '',
        contactPerson: org.contact_person || '',
        phone: org.contact_number || org.primary_phone || '',
        address: org.address || ''
      }));
    }
  };

  const handlePromote = async () => {
    try {
      setLoading(true);
      setError(null);

      if (formData.vatRegistered && formData.trn && formData.trn.replace(/\s/g, '').length !== 15) {
        setError('TRN must be exactly 15 digits');
        return;
      }

      if (!selectedCustomerTcId) return;
      if (!matchedOrganization?.id) {
        setCodeMappingError('Please enter correct code');
        return;
      }

      const payload = {
        name: formData.name,
        code: formData.code,
        email: formData.email,
        contact_person: formData.contactPerson,
        contact_number: formData.phone,
        address: formData.address,
        organization_type: 'TYPING_CENTER',
        organization_id: matchedOrganization.id,
        ...vatPayload(formData),
        active: true
      };

      const response = await ApiService.post(`/api/typing-centers/promote/${selectedCustomerTcId}`, payload);
      if (response.data && response.data.success) {
        await fetchTypingCenters();
        setOpenDialog(false);
        resetForm();
        navigate(`/typing-centers/${response.data.data.id}`);
      }
    } catch (err) {
      console.error('Error promoting customer typing center:', err);
      setError(err.response?.data?.message || 'Failed to promote typing center');
    } finally {
      setLoading(false);
    }
  };

  const getCenterStats = (centerId) => {
    // Placeholder for now - will be populated from backend later
    return {
      organizations: 0,
      users: 0
    };
  };

  // Determine page title based on filter
  const getPageTitle = () => {
    if (!isAdmin) return 'My Typing Center';
    if (typeFilter === 'TYPING_CENTER') return 'Typing Centers';
    if (typeFilter === 'PRO') return 'PRO';
    if (typeFilter === 'B2B') return 'Providers';
    return 'Tenants';
  };

  const visibleCenters = typingCenters.filter((center) => {
    if (promotionFilter === 'ALL') return true;
    const isPromoted = Boolean(center.promoted_from_internal_org_id || center.promotedFromInternalOrgId);
    return promotionFilter === 'PROMOTED' ? isPromoted : !isPromoted;
  });

  const getButtonLabel = () => {
    if (typeFilter === 'TYPING_CENTER') return 'Add Typing Center';
    if (typeFilter === 'PRO') return 'Add PRO';
    if (typeFilter === 'B2B') return 'Add Provider';
    return 'Add Tenant';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.main' }}>
          {getPageTitle()}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={tenantView}
            onChange={(e, next) => {
              if (!next) return;
              setTenantView(next);
              try { localStorage.setItem('typingCentersView', next); } catch (err) { /* private mode */ }
            }}
          >
            <ToggleButton value="cards" aria-label="Card view">
              <Tooltip title="Card view"><CardViewIcon fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table view">
              <Tooltip title="Table view"><TableViewIcon fontSize="small" /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          {isAdmin && (
            <>
            {typeFilter === 'TYPING_CENTER' && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ fontSize: '0.875rem' }}>Show</InputLabel>
                <Select
                  value={promotionFilter}
                  label="Show"
                  onChange={(e) => setPromotionFilter(e.target.value)}
                  sx={{ fontSize: '0.875rem' }}
                >
                  <MenuItem value="ALL" sx={{ fontSize: '0.875rem' }}>All</MenuItem>
                  <MenuItem value="INDEPENDENT" sx={{ fontSize: '0.875rem' }}>Independent</MenuItem>
                  <MenuItem value="PROMOTED" sx={{ fontSize: '0.875rem' }}>Promoted</MenuItem>
                </Select>
              </FormControl>
            )}
            {typeFilter === 'TYPING_CENTER' && (
              <Button
                variant="outlined"
                startIcon={<UpgradeIcon />}
                onClick={openPromoteDialog}
                sx={{ textTransform: 'none', fontSize: '0.875rem' }}
                disabled={loading}
              >
                Promote TC
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
              sx={{ textTransform: 'none', fontSize: '0.875rem' }}
              disabled={loading}
            >
              {getButtonLabel()}
            </Button>
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && typingCenters.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : tenantView === 'table' ? (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead sx={getBrandedTableHeadSx(theme)}>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.8rem' }}>Name</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>Type</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>Status</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>Contact Person</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>Email</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>Phone</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>Address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleCenters.map((center) => {
                  const orgType = center.organization_type || center.organizationType;
                  const isPromoted = Boolean(center.promoted_from_internal_org_id || center.promotedFromInternalOrgId);
                  return (
                    <TableRow
                      key={center.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/typing-centers/${center.id}`)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                              {center.name}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                              {center.code}
                            </Typography>
                          </Box>
                          {isPromoted && (
                            <Chip label="Promoted" color="secondary" size="small" sx={{ fontSize: '0.7rem', height: '20px', fontWeight: 700 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {orgType && (
                          <Chip
                            label={orgType === 'PRO' ? 'PRO' : orgType === 'B2B' ? 'PROVIDER' : 'TYPING CENTER'}
                            size="small"
                            color={orgType === 'PRO' ? 'info' : 'default'}
                            sx={{ fontSize: '0.7rem', height: '20px', fontWeight: 700 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusOf(center)}
                          color={statusOf(center) === 'ACTIVE' ? 'success' : 'default'}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: '20px' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{center.contact_person || center.contactPerson || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{center.email || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{center.phone || center.contact_number || center.contactNumber || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{center.address || '-'}</TableCell>
                    </TableRow>
                  );
                })}
                {visibleCenters.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: '0.875rem' }}>
                      No {getPageTitle().toLowerCase()} found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Grid container spacing={3}>
        {visibleCenters
          .map((center) => {
          const stats = getCenterStats(center.id);
          const orgType = center.organization_type || center.organizationType;
          const orgTypeLabel = orgType === 'PRO' ? 'PRO' : 'TYPING CENTER';
          const isPromoted = Boolean(center.promoted_from_internal_org_id || center.promotedFromInternalOrgId);

          return (
            <Grid item xs={12} sm={6} md={4} key={center.id}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => navigate(`/typing-centers/${center.id}`)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <BusinessIcon sx={{ color: 'secondary.main', mr: 1, fontSize: 32 }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: '1rem' }}>
                        {center.name}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        {center.code}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isPromoted && (
                        <Chip
                          label="Promoted"
                          color="secondary"
                          size="small"
                          sx={{ fontSize: '0.7rem', height: '22px', fontWeight: 700 }}
                        />
                      )}
                      <Chip
                        label={statusOf(center)}
                        color={statusOf(center) === 'ACTIVE' ? 'success' : 'default'}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: '22px' }}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                        Email
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        {center.email}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                        Contact Person
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        {center.contact_person || center.contactPerson || '-'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                        Phone
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        {center.phone || center.contact_number || center.contactNumber || '-'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                        Mapped Organizations
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'secondary.main' }}>
                        {stats.organizations}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                        Users
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'success.main' }}>
                        {stats.users}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {center.address}
                  </Typography>

                  {orgType && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <Chip
                        label={orgTypeLabel}
                        size="small"
                        color={orgType === 'PRO' ? 'info' : 'default'}
                        sx={{ fontSize: '0.7rem', height: '22px', fontWeight: 700 }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        </Grid>
      )}

      {/* Create / Promote Typing Center Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {dialogMode === 'promote'
            ? 'Promote Customer Typing Center'
            : typeFilter === 'PRO' ? 'Add New PRO' : typeFilter === 'TYPING_CENTER' ? 'Add New Typing Center' : 'Add New Tenant'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {dialogMode === 'promote' && (
                <>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: '0.875rem' }}>Provider</InputLabel>
                      <Select
                        value={selectedB2bId}
                        label="Provider"
                        onChange={(e) => { setSelectedB2bId(e.target.value); setSelectedCustomerTcId(''); }}
                        sx={{ fontSize: '0.875rem' }}
                      >
                        {b2bList.map((b2b) => (
                          <MenuItem key={b2b.id} value={b2b.id} sx={{ fontSize: '0.875rem' }}>
                            {b2b.name} ({b2b.code})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small" disabled={!selectedB2bId || customerTcsLoading}>
                      <Autocomplete
                        size="small"
                        options={customerTcs}
                        getOptionLabel={(o) => `${o?.name || ''}${promotedOrgIds.has(o?.id) ? ' (already promoted)' : ''}`}
                        getOptionDisabled={(o) => promotedOrgIds.has(o?.id)}
                        isOptionEqualToValue={(o, v) => o?.id === v?.id}
                        value={selectedCustomerTc}
                        onChange={(e, value) => {
                          setSelectedCustomerTc(value);
                          handleSelectCustomerTc(value);
                        }}
                        onInputChange={onCustomerTcInputChange}
                        filterOptions={customerTcFilterOptions}
                        loadingText="Searching..."
                        noOptionsText="No organizations found"
                        loading={customerTcsLoading}
                        renderInput={(params) => (
                          <TextField {...params} label="Customer TC" placeholder="Search by name or TRN..." />
                        )}
                      />
                    </FormControl>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Center Code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  size="small"
                  placeholder="e.g., TC-DXB-002"
                  error={Boolean(codeMappingError)}
                  helperText={codeMappingError || ''}
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              {/* Show organization type dropdown only if no filter is active */}
              {!typeFilter && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '0.875rem' }}>Organization Type</InputLabel>
                    <Select
                      value={formData.organizationType}
                      label="Organization Type"
                      onChange={(e) => setFormData({ ...formData, organizationType: e.target.value })}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      <MenuItem value="TYPING_CENTER" sx={{ fontSize: '0.875rem' }}>
                        Typing Center
                      </MenuItem>
                      <MenuItem value="PRO" sx={{ fontSize: '0.875rem' }}>
                        PRO
                      </MenuItem>
                      <MenuItem value="B2B" sx={{ fontSize: '0.875rem' }}>Provider</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mt: 0.5,
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.875rem'
                  }}
                >
                  Organization Details
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Center Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  size="small"
                  multiline
                  rows={2}
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              {/* BE-31: VAT setup - the ledger starts at the current filing period */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch size="small" checked={formData.vatRegistered}
                    onChange={(e) => setFormData({ ...formData, vatRegistered: e.target.checked })} />}
                  label={<Typography sx={{ fontSize: '0.875rem' }}>VAT registered</Typography>}
                />
              </Grid>
              {formData.vatRegistered && (
                <>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth size="small" label="TRN" value={formData.trn}
                      onChange={(e) => setFormData({ ...formData, trn: e.target.value })}
                      InputLabelProps={{ sx: { fontSize: '0.875rem' } }} inputProps={{ sx: { fontSize: '0.875rem' } }} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField select fullWidth size="small" label="VAT return" value={formData.vatPeriodMonths}
                      onChange={(e) => setFormData({ ...formData, vatPeriodMonths: e.target.value })}
                      InputLabelProps={{ sx: { fontSize: '0.875rem' } }} SelectProps={{ sx: { fontSize: '0.875rem' } }}>
                      <MenuItem value={1}>Monthly</MenuItem>
                      <MenuItem value={3}>Quarterly</MenuItem>
                    </TextField>
                  </Grid>
                  {formData.vatPeriodMonths === 3 && (
                    <Grid item xs={12} md={4}>
                      <TextField select fullWidth size="small" label="Quarters start in" value={formData.vatFirstPeriodMonth}
                        onChange={(e) => setFormData({ ...formData, vatFirstPeriodMonth: e.target.value })}
                        InputLabelProps={{ sx: { fontSize: '0.875rem' } }} SelectProps={{ sx: { fontSize: '0.875rem' } }}>
                        <MenuItem value={1}>January</MenuItem>
                        <MenuItem value={2}>February</MenuItem>
                        <MenuItem value={3}>March</MenuItem>
                      </TextField>
                    </Grid>
                  )}
                </>
              )}
              {mapsOneOrg(formData.organizationType) && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      pt: 1,
                      mt: 0.5,
                      borderTop: `1px solid ${theme.palette.divider}`
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      Mapped Organization
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      {organizationsLoading ? 'Loading...' : (matchedOrganization?.name || '-')}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ textTransform: 'none', fontSize: '0.875rem' }}>
            Cancel
          </Button>
          <Button
            onClick={dialogMode === 'promote' ? handlePromote : handleCreateCenter}
            variant="contained"
            disabled={
              !formData.name ||
              !formData.code ||
              (dialogMode === 'promote' && !selectedCustomerTcId) ||
              (mapsOneOrg(formData.organizationType) && (!matchedOrganization?.id || organizationsLoading))
            }
            sx={{ textTransform: 'none', fontSize: '0.875rem' }}
          >
            {dialogMode === 'promote' ? 'Promote' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TypingCentersList;
